import React, { useMemo } from "react";
import styled from "styled-components";
import { TimePickerInput } from "@shared/EDS/components/TimePickerInput";
import { RepeatOnHold } from "@shared/EDS/RepeatOnHold";
import { DateTime, Duration } from "luxon";
import { useSelectedTimeStore } from "../contexts/SelectedTime/useSelectedTimeStore";
import { Select } from "@shared/EDS/components/Select";
import { getDatesFromInterval } from "../utils/luxonHelpers";

export interface DayAndTimeSelectProps {
  readonly style?: React.CSSProperties;
  readonly className?: string;
  readonly showNavigateButtons?: boolean;
}

function LocalDayAndTimeSelect({
  className,
  style,
  showNavigateButtons = true,
}: DayAndTimeSelectProps): React.JSX.Element {
  const selectedTime = useSelectedTimeStore((state) => state.selectedTime);
  const scenarioInterval = useSelectedTimeStore((state) => state.scenarioInterval);

  const datesInInterval = useMemo<DateTime[]>(
    () => (scenarioInterval ? getDatesFromInterval(scenarioInterval) : []),
    [scenarioInterval],
  );

  const delta = Duration.fromObject({ minutes: 1 });
  const handleTimeToStart = useSelectedTimeStore((state) => state.moveToStart);
  const handleTimeDecrease = useSelectedTimeStore((state) => state.decrease);
  const handleTimeIncrease = useSelectedTimeStore((state) => state.increase);
  const handleTimeToEnd = useSelectedTimeStore((state) => state.moveToEnd);
  const setDate = useSelectedTimeStore((state) => state.setDate);
  const setDateTime = useSelectedTimeStore((state) => state.setDateTime);
  const handleDatePickerChange = (date: string) => setDate(DateTime.fromISO(date));
  const handleTimePickerChange = (time?: DateTime) => time && setDateTime(time);

  return (
    <div className={className} style={style}>
      {showNavigateButtons && (
        <>
          <tractie-button
            data-testid="day-time-select-start-button"
            level="secondary"
            onClick={handleTimeToStart}
            icon-only
          >
            <tractie-icon name="chevron-double-left" />
          </tractie-button>
          <RepeatOnHold onTrigger={() => handleTimeDecrease(delta)} intervalInMs={400}>
            <tractie-button
              data-testid="day-time-select-decrease-button"
              level="secondary"
              icon-only
            >
              <tractie-icon name="chevron-left" />
            </tractie-button>
          </RepeatOnHold>
        </>
      )}
      <Select
        style={{ width: "96px", height: "40px" }}
        ucFirst={true}
        placeHolder=""
        items={datesInInterval.map((day) => ({
          label: day.toFormat("ccc"),
          value: day.toISODate()!,
        }))}
        value={selectedTime?.toISODate()}
        onValueChange={handleDatePickerChange}
      />
      <TimePickerInput
        width={96}
        value={selectedTime}
        allowedInterval={scenarioInterval}
        onValueChange={handleTimePickerChange}
      />
      {showNavigateButtons && (
        <>
          <RepeatOnHold onTrigger={() => handleTimeIncrease(delta)} intervalInMs={400}>
            <tractie-button
              data-testid="day-time-select-increase-button"
              level="secondary"
              icon-only
            >
              <tractie-icon name="chevron-right" />
            </tractie-button>
          </RepeatOnHold>
          <tractie-button
            data-testid="day-time-select-end-button"
            level="secondary"
            onClick={handleTimeToEnd}
            icon-only
          >
            <tractie-icon name="chevron-double-right" />
          </tractie-button>
        </>
      )}
    </div>
  );
}

export const DayAndTimeSelect = styled(LocalDayAndTimeSelect)`
  align-items: center;
  display: flex;
  gap: 5px;
  justify-content: center;
  z-index: 30;
`;
